import React, { useState } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { useMatchStore } from '../data/matchStore';
import { allPartiesIncludingNonParliamentary as PARTIES } from '../data/parties';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw, Check, X as XIcon, ChevronRight } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import confetti from 'canvas-confetti';

export default function MatchPage() {
  const { lang, isRtl } = useI18n();
  const { propositions, isMatchEnabled, recordPlay, itemsPerSession, disclaimer } = useMatchStore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [direction, setDirection] = useState(0); // 1 = right, -1 = left, 0 = neutral
  const [sessionPropositions, setSessionPropositions] = useState<any[]>([]);

  React.useEffect(() => {
    initSession();
  }, [propositions, itemsPerSession]);

  const initSession = (previousSession: any[] = []) => {
    const validProps = propositions.filter(p => p.status === 'valide');
    if (validProps.length <= itemsPerSession) {
      setSessionPropositions([...validProps].sort(() => 0.5 - Math.random()));
      return;
    }
    const retainCount = Math.min(previousSession.length, Math.floor(Math.random() * 2) + 2);
    const retained = previousSession.slice(0, retainCount);
    const remainingProps = validProps.filter(p => !retained.find((r: any) => r.id === p.id));
    const shuffled = [...remainingProps].sort(() => 0.5 - Math.random());
    const newItems = shuffled.slice(0, itemsPerSession - retainCount);
    setSessionPropositions([...retained, ...newItems].sort(() => 0.5 - Math.random()));
  };

  const themeTranslations: Record<string, string> = {
    "Éducation": "التعليم",
    "Santé": "الصحة",
    "Économie": "الاقتصاد",
    "Environnement": "البيئة",
    "Social": "اجتماعي",
    "Justice": "العدالة",
    "Emploi": "التشغيل",
    "Libertés": "الحريات",
    "Institutions": "المؤسسات",
    "Culture": "الثقافة",
    "Politique": "سياسة"
  };

  // Si l'admin a désactivé le module, on redirige
  if (!isMatchEnabled) {
    return <Navigate to="/" replace />;
  }

  const isFinished = sessionPropositions.length > 0 && currentIndex >= sessionPropositions.length;

  const handleSwipe = (swipeDir: number, partyIds: string[]) => {
    if (swipeDir === 1) {
      // Swipe Right = D'accord = +1 point pour chaque parti
      setScores(prev => {
        const next = { ...prev };
        partyIds.forEach(id => {
          next[id] = (next[id] || 0) + 1;
        });
        return next;
      });
    }
    setDirection(swipeDir);
    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
      setDirection(0);
    }, 200);
  };

  const handleDragEnd = (e: any, info: PanInfo, partyIds: string[]) => {
    const swipeThreshold = 100;
    if (info.offset.x > swipeThreshold) {
      handleSwipe(1, partyIds);
    } else if (info.offset.x < -swipeThreshold) {
      handleSwipe(-1, partyIds);
    }
  };

  const resetMatch = () => {
    setCurrentIndex(0);
    setScores({});
    setDirection(0);
    initSession(sessionPropositions);
  };

  // Effet pour enregistrer les stats une fois le match terminé
  React.useEffect(() => {
    if (isFinished) {
      const sortedParties = Object.keys(scores)
        .map(partyId => ({ partyId, score: scores[partyId] }))
        .sort((a, b) => b.score - a.score)
        .filter(p => p.score > 0);
      const podiumIds = sortedParties.slice(0, 3).map(p => p.partyId);
      if (podiumIds.length > 0) {
        recordPlay(podiumIds);
        
        // Célébration (Confetti)
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#22c55e', '#eab308', '#3b82f6', '#f97316'],
          zIndex: 100
        });
        setTimeout(() => {
          confetti({ particleCount: 100, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#22c55e', '#eab308'], zIndex: 100 });
          confetti({ particleCount: 100, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#3b82f6', '#f97316'], zIndex: 100 });
        }, 800);
      }
    }
  }, [isFinished]); // Ne s'exécute qu'une fois quand isFinished passe à true

  const renderPodium = () => {
    // Trier les partis par score décroissant
    const sortedParties = Object.keys(scores)
      .map(partyId => ({ partyId, score: scores[partyId] }))
      .sort((a, b) => b.score - a.score)
      .filter(p => p.score > 0); // Exclure ceux avec 0 point

    if (sortedParties.length === 0) {
      return (
        <div className="text-center">
          <p className="text-gray-500 mb-6">Vous n'avez été d'accord avec aucune proposition.</p>
          <Button onClick={resetMatch}>Recommencer le test</Button>
        </div>
      );
    }

    // Distribution stricte : max 2 partis par marche, pas de marche vide
    const finalPodium: string[][] = [[], [], []];
    let currentStep = 0;
    let lastScore = sortedParties[0]?.score;

    for (const p of sortedParties) {
        if (currentStep > 2) break;
        if (p.score < lastScore) {
            currentStep++; 
            if (currentStep > 2) break;
            lastScore = p.score;
        }
        if (finalPodium[currentStep].length >= 2) {
            currentStep++; 
            if (currentStep > 2) break;
            lastScore = p.score; 
        }
        finalPodium[currentStep].push(p.partyId);
    }

    // Remplissage des marches vides avec des partis aléatoires
    const usedParties = finalPodium.flat();
    const randomParties = [...PARTIES].sort(() => 0.5 - Math.random());
    
    for (let i = 0; i < 3; i++) {
        if (finalPodium[i].length === 0) {
            const p = randomParties.find(rp => !usedParties.includes(rp.acronym));
            if (p) {
                finalPodium[i].push(p.acronym);
                usedParties.push(p.acronym);
            }
        }
    }

    const getPartyInfo = (id: string) => PARTIES.find(p => p.acronym === id);

    const renderLogos = (parties: string[] | undefined, sizeClass: string, borderColor: string) => {
      if (!parties || parties.length === 0) return null;
      return (
        <div className={`flex justify-center -space-x-3 z-10 mb-[-20px] ${parties.length > 1 ? 'scale-90' : ''}`}>
          {parties.slice(0, 3).map((pid, idx) => (
            <div key={pid} className={`${sizeClass} rounded-full bg-white shadow-lg border-4 ${borderColor} p-1 md:p-2 relative`} style={{ zIndex: 30 - idx }}>
               <img src={getPartyInfo(pid)?.logoUrl} alt={pid} className="w-full h-full object-contain" />
            </div>
          ))}
          {parties.length > 3 && (
             <div className={`${sizeClass} rounded-full bg-gray-100 shadow-lg border-4 ${borderColor} flex items-center justify-center text-xs font-bold text-gray-500 relative`} style={{ zIndex: 0 }}>
               +{parties.length - 3}
             </div>
          )}
        </div>
      );
    };

    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-black font-heading text-gray-900 mb-8">Votre Podium 🎉</h2>
        
        <div className="flex items-end justify-center gap-2 md:gap-4 h-64 mb-12 w-full max-w-md">
          {/* Position 2 (Argent) */}
          {finalPodium[1].length > 0 && (
            <div className="flex flex-col items-center flex-1">
              {renderLogos(finalPodium[1], "w-16 h-16 md:w-20 md:h-20", "border-gray-300")}
              <div className="w-full bg-gray-200 rounded-t-lg h-[50%] flex flex-col items-center justify-start pt-8">
                <span className="text-2xl font-black text-gray-400">2</span>
                <span className="text-xs font-bold mt-1 text-center px-1 text-balance">
                  {finalPodium[1].join(', ')}
                </span>
              </div>
            </div>
          )}

          {/* Position 1 (Or) */}
          <div className="flex flex-col items-center flex-1">
            {renderLogos(finalPodium[0], "w-20 h-20 md:w-24 md:h-24", "border-yellow-400")}
            <div className="w-full bg-yellow-100 rounded-t-lg h-[80%] flex flex-col items-center justify-start pt-10 border border-yellow-200">
              <span className="text-3xl font-black text-yellow-600">1</span>
              <span className="text-sm font-bold mt-1 text-yellow-800 text-center px-1 text-balance">
                {finalPodium[0].join(', ')}
              </span>
            </div>
          </div>

          {/* Position 3 (Bronze) */}
          {finalPodium[2].length > 0 && (
            <div className="flex flex-col items-center flex-1">
              {renderLogos(finalPodium[2], "w-14 h-14 md:w-16 md:h-16", "border-amber-700")}
              <div className="w-full bg-orange-50 rounded-t-lg h-[30%] flex flex-col items-center justify-start pt-6 border border-orange-100">
                <span className="text-xl font-black text-amber-700">3</span>
                <span className="text-xs font-bold mt-1 text-amber-900 text-center px-1 text-balance">
                  {finalPodium[2].join(', ')}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 w-full max-w-sm relative z-20">
          <Button asChild className="w-full py-6 text-lg rounded-xl shadow-lg">
            <Link to="/partis">
              Découvrir les programmes <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
          <Button variant="outline" className="w-full rounded-xl" onClick={resetMatch}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refaire le test
          </Button>
        </div>

        {/* Mention animée */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-8 text-center max-w-sm px-4"
        >
          <p className="text-[10px] sm:text-xs text-gray-400 font-medium leading-relaxed bg-gray-100/50 p-3 rounded-lg border border-gray-200/50 text-justify text-balance" dir={isRtl ? 'rtl' : 'ltr'}>
            {lang === 'ar' ? disclaimer?.ar : disclaimer?.fr}
          </p>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative overflow-hidden">
      {/* Navbar Minimaliste */}
      <div className="w-full px-6 py-4 flex items-center justify-between z-10 relative" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex-1">
          <Button variant="ghost" className="font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />
              {lang === 'ar' ? 'العودة' : lang === 'zgh' ? 'ⵓⵖⴰⵍ' : 'Retour'}
            </Link>
          </Button>
        </div>
        
        <span className="font-heading font-black text-base md:text-xl text-primary flex flex-col md:flex-row items-center gap-1 md:gap-2 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-max text-center">
          <span>Match ton Parti</span>
          <span className="font-arabic font-black text-sm md:text-xl">اكتشف الحزب السياسي لقريب ليك</span>
        </span>

        <div className="flex-1 flex justify-end">
          {!isFinished && sessionPropositions.length > 0 && (
            <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
              {currentIndex + 1} / {sessionPropositions.length}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {isFinished || sessionPropositions.length === 0 ? (
          renderPodium()
        ) : (
          <div className="relative w-full max-w-sm aspect-[3/4] flex items-center justify-center">
            <AnimatePresence>
              <motion.div
                key={currentIndex}
                initial={{ scale: 0.95, opacity: 0, y: 50 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ 
                  x: direction === 1 ? 300 : direction === -1 ? -300 : 0, 
                  opacity: 0, 
                  rotate: direction === 1 ? 15 : direction === -1 ? -15 : 0 
                }}
                transition={{ duration: 0.3 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={(e, info) => handleDragEnd(e, info, sessionPropositions[currentIndex].partyIds)}
                className="absolute inset-0 bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 flex flex-col justify-center text-center cursor-grab active:cursor-grabbing"
              >
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex gap-2">
                    <span>{sessionPropositions[currentIndex].theme}</span>
                    <span className="opacity-50">|</span>
                    <span className="font-arabic font-black lowercase tracking-normal">{themeTranslations[sessionPropositions[currentIndex].theme] || sessionPropositions[currentIndex].theme}</span>
                  </span>
                </div>
                
                <p className="text-2xl font-bold text-gray-900 leading-snug mt-8 font-arabic text-balance" dir="rtl">
                  "{sessionPropositions[currentIndex].textAr}"
                </p>

                <h3 className="text-lg text-gray-500 font-medium leading-snug mt-6 text-balance">
                  "{sessionPropositions[currentIndex].textFr}"
                </h3>

                <div className="absolute bottom-6 left-0 right-0 px-6 flex justify-between items-center z-10 pointer-events-none">
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleSwipe(-1, sessionPropositions[currentIndex].partyIds); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="w-14 h-14 rounded-full bg-white shadow-md border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-50 transition-colors pointer-events-auto"
                  >
                     <XIcon className="w-8 h-8"/>
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleSwipe(1, sessionPropositions[currentIndex].partyIds); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="w-14 h-14 rounded-full bg-white shadow-md border border-green-100 text-green-500 flex items-center justify-center hover:bg-green-50 transition-colors pointer-events-auto"
                  >
                     <Check className="w-8 h-8"/>
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
